import { useCallback, useEffect, useState } from "react";
import {
    AudienceSegment,
    ordersAnalyticsApi,
} from "@/features/orders-analytics/api/orders-analytics.api";
import {
    CustomerIntelligenceResponse,
    OrdersAnalyticsFilters,
} from "@/features/orders-analytics/types/orders-analytics.types";

export function useCustomerIntelligence(filters: OrdersAnalyticsFilters) {
    const [data, setData] = useState<CustomerIntelligenceResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [audienceError, setAudienceError] = useState<string | null>(null);
    const [audienceCount, setAudienceCount] = useState<number | null>(null);
    const [audienceBlob, setAudienceBlob] = useState<Blob | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        ordersAnalyticsApi
            .fetchCustomerIntelligence(filters)
            .then((response) => {
                if (cancelled) return;
                setData(response);
                setLastUpdatedAt(Date.now());
            })
            .catch((reason: unknown) => {
                if (cancelled) return;
                setError(reason instanceof Error ? reason.message : "Unknown error");
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [filters, reloadKey]);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    const exportAudience = useCallback(
        async (exportFilters: OrdersAnalyticsFilters, segment: AudienceSegment) => {
            try {
                setAudienceError(null);
                setAudienceCount(null);
                setAudienceBlob(null);
                setAudienceLoading(true);
                const { blob, count } = await ordersAnalyticsApi.prepareAudienceCsv(
                    exportFilters,
                    segment
                );
                setAudienceBlob(blob);
                setAudienceCount(count);
            } catch (reason) {
                setAudienceError(reason instanceof Error ? reason.message : "Unknown error");
            } finally {
                setAudienceLoading(false);
            }
        },
        []
    );

    const saveAudience = useCallback(
        (fileName: string) => {
            if (audienceBlob !== null) {
                ordersAnalyticsApi.saveCsvBlob(audienceBlob, fileName);
            }
        },
        [audienceBlob]
    );

    const clearAudience = useCallback(() => {
        setAudienceBlob(null);
        setAudienceCount(null);
        setAudienceError(null);
    }, []);

    return {
        data,
        loading,
        error,
        lastUpdatedAt,
        refresh,
        exportAudience,
        saveAudience,
        clearAudience,
        audienceLoading,
        audienceError,
        audienceCount,
        audienceReady: audienceBlob !== null,
    };
}
